import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { getViewportForBounds, useNodesInitialized, useReactFlow } from '@xyflow/react';
import {
  INITIAL_FIT_PADDING,
  INITIAL_LAYOUT_REVEAL_DELAY,
  INITIAL_LAYOUT_STACK_SETTLE_DELAY,
  IS_VC_COUNTRY_GROUPING_ENABLED,
  REACT_FLOW,
  VALUE_CHAIN_MAP,
} from 'constants/general';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from 'constants/interfaces';

import { applyCountryGroupingToNodes } from '../utils/valueChainNodeLayout';

import type { IGetValueChainBoardResponse } from 'store/slices/valueChainMap/interfaces/IGetValueChainBoardResponse';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';
import type { IUseValueChainInitialLayoutParams } from '../interfaces/IUseValueChainInitialLayout';

const useValueChainInitialLayout = ({
  nodes,
  isLoading,
  boardData,
  setNodes,
  nodesRef,
}: IUseValueChainInitialLayoutParams) => {
  const {
    getNodesBounds,
    setViewport,
    viewportInitialized,
  } = useReactFlow();
  const areNodesInitialized = useNodesInitialized({ includeHiddenNodes: false });

  const [isInitialViewportReady, setIsInitialViewportReady] = useState(false);
  const [isViewportSizeReady, setIsViewportSizeReady] = useState(false);
  const initialLayoutTimersRef = useRef<number[]>([]);
  const hasStartedInitialLayoutRef = useRef(false);
  const hasCompletedInitialLayoutRef = useRef(false);
  const groupedDataRef = useRef<IGetValueChainBoardResponse | undefined>(undefined);
  const viewportSizeRef = useRef<{ width: number, height: number } | undefined>(undefined);
  const viewportXRef = useRef(0);
  const viewportYRef = useRef(0);
  const viewportZoomRef = useRef(1);
  const panDxRef = useRef(0);
  const panDyRef = useRef(0);
  const panDecayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearInitialLayoutTimers = useCallback(() => {
    initialLayoutTimersRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    initialLayoutTimersRef.current = [];
  }, []);

  const runInitialFitView = useCallback(() => {
    const currentNodes = nodesRef.current;
    const currentViewportSize = viewportSizeRef.current;

    if (!currentViewportSize) {
      return;
    }

    const boardNodes = currentNodes.filter(
      (node) => node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD,
    );
    const visibleUnitNodes = currentNodes.filter((node) => {
      if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
        return false;
      }

      const nodeData = node.data as unknown as ICustomUnitNodeData;
      return !nodeData.isHiddenByCountryGroup && !nodeData.isHiddenByParent;
    });

    let nodesToFit = currentNodes;

    if (boardNodes.length > 0) {
      nodesToFit = boardNodes;
    } else if (visibleUnitNodes.length > 0) {
      nodesToFit = visibleUnitNodes;
    }

    if (!nodesToFit.length) {
      return;
    }

    const fitNodesBounds = getNodesBounds(nodesToFit);
    const baseViewport = getViewportForBounds(
      fitNodesBounds,
      currentViewportSize.width,
      currentViewportSize.height,
      REACT_FLOW.MIN_ZOOM,
      REACT_FLOW.MAX_ZOOM,
      INITIAL_FIT_PADDING,
    );
    const nextZoom = Math.max(baseViewport.zoom * 0.96, REACT_FLOW.MIN_ZOOM);
    const topAnchorNodes = visibleUnitNodes.length > 0 ? visibleUnitNodes : nodesToFit;
    const topAnchorBounds = getNodesBounds(topAnchorNodes);
    const topOffset = VALUE_CHAIN_MAP.TITLE_HEIGHT * 2 + VALUE_CHAIN_MAP.BOARD_PADDING * 2;
    const nextX = (currentViewportSize.width - fitNodesBounds.width * nextZoom) / 2
      - fitNodesBounds.x * nextZoom;
    const nextY = topOffset - topAnchorBounds.y * nextZoom;

    setViewport({
      x: nextX,
      y: nextY,
      zoom: nextZoom,
    }, { duration: 0 });
  }, [getNodesBounds, nodesRef, setViewport]);

  const handleViewportSizeReady = useCallback(() => {
    setIsViewportSizeReady(true);
  }, []);

  const handleViewportSync = useCallback((x: number, y: number, zoom: number) => {
    panDxRef.current = x - viewportXRef.current;
    panDyRef.current = y - viewportYRef.current;
    viewportXRef.current = x;
    viewportYRef.current = y;
    viewportZoomRef.current = zoom;

    if (panDecayTimerRef.current) {
      clearTimeout(panDecayTimerRef.current);
    }
    panDecayTimerRef.current = setTimeout(() => {
      panDxRef.current = 0;
      panDyRef.current = 0;
    }, 200);
  }, []);

  const handleViewportSizeSync = useCallback((size: { width: number, height: number }) => {
    viewportSizeRef.current = size;
  }, []);

  const handleGroupedDataSync = useCallback((data: IGetValueChainBoardResponse | undefined) => {
    groupedDataRef.current = data;
  }, []);

  useEffect(() => () => {
    clearInitialLayoutTimers();
  }, [clearInitialLayoutTimers]);

  useEffect(() => {
    if (
      isLoading
      || !boardData
      || boardData.data.boards.length > 0
      || hasCompletedInitialLayoutRef.current
    ) {
      return;
    }

    hasStartedInitialLayoutRef.current = true;
    hasCompletedInitialLayoutRef.current = true;
    setIsInitialViewportReady(true);
  }, [boardData, isLoading, nodes.length]);

  useEffect(() => {
    if (
      hasStartedInitialLayoutRef.current
      || hasCompletedInitialLayoutRef.current
      || isLoading
      || !isViewportSizeReady
      || !viewportSizeRef.current
      || !viewportInitialized
      || !areNodesInitialized
      || nodes.length === 0
    ) {
      return;
    }

    hasStartedInitialLayoutRef.current = true;
    setIsInitialViewportReady(false);

    if (IS_VC_COUNTRY_GROUPING_ENABLED) {
      setNodes((prevNodes) => applyCountryGroupingToNodes(prevNodes));
    }

    const prefitTimerId = window.setTimeout(() => {
      runInitialFitView();
    }, 50);

    const finalFitTimerId = window.setTimeout(() => {
      runInitialFitView();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const revealTimerId = window.setTimeout(() => {
            clearInitialLayoutTimers();
            hasCompletedInitialLayoutRef.current = true;
            setIsInitialViewportReady(true);
          }, INITIAL_LAYOUT_REVEAL_DELAY);

          initialLayoutTimersRef.current.push(revealTimerId);
        });
      });
    }, INITIAL_LAYOUT_STACK_SETTLE_DELAY);

    initialLayoutTimersRef.current.push(prefitTimerId, finalFitTimerId);
  }, [
    areNodesInitialized,
    clearInitialLayoutTimers,
    isLoading,
    isViewportSizeReady,
    nodes.length,
    runInitialFitView,
    setNodes,
    viewportInitialized,
  ]);

  return {
    isInitialViewportReady,
    viewportSizeRef,
    viewportXRef,
    viewportYRef,
    viewportZoomRef,
    panDxRef,
    panDyRef,
    groupedDataRef,
    hasCompletedInitialLayoutRef,
    handleViewportSizeReady,
    handleViewportSync,
    handleViewportSizeSync,
    handleGroupedDataSync,
  };
};

export default useValueChainInitialLayout;
